import React, {useCallback} from 'react';
import {groupBy, some} from 'lodash';
import classNames from 'classnames';
import {useDispatch, useSelector} from 'react-redux';
import {useMediaQuery} from 'src/hooks/useMediaQuery';
import {sortCollegeSemesters} from 'shared/portfolio/utils';
import {newCollegeCourse} from 'shared/portfolio/actions/college-courses';
import {Store} from 'src/types/store';

import TwoColumnMasonry from 'shared/portfolio/components/two-column-masonry';
import FormButton from 'src/components/button';
import CollegeCourseFormCard from 'shared/portfolio/components/courses/college-courses/college-course-form-card';
import Card from '../../card';
import PortfolioSectionHeader from '../../portfolio-section-header';
import Section from '../../section';
import CollegeCourse from '../college-course';
import SchoolYearHeader from '../school-year-header';

import styles from './styles.module.scss';
import PortfolioIcon from '../../portfolio-icon/portfolio-icon';

const CollegeCourses = () => {
  const mq = useMediaQuery();
  const dispatch = useDispatch();
  const collegeCourses = useSelector((state: Store) => state.collegeCourses);
  const currentInstitution = useSelector((state: Store) => state.student.currentInstitution);
  const editing = some(collegeCourses, 'editing');
  const handleAddClick = useCallback(
    () => dispatch(newCollegeCourse(currentInstitution)),
    [dispatch, currentInstitution]
  );
  const coursesForSemester = groupBy(collegeCourses, (course) =>
    course.new ? 'new' : `${course.year} ${course.semester}`
  );
  const semesterGroups = sortCollegeSemesters(Object.keys(coursesForSemester));
  const currentYear = new Date().getFullYear();

  return (
    <Section>
      <div id="courses" className={styles.collegeCourseDiv}></div>
      <PortfolioSectionHeader
        title="Courses"
        showAddCourseButton={mq.XS}
        displayNew={!editing && !(mq.XS && !Object.keys(collegeCourses).length)}
        sectionName="courses"
        addNew={handleAddClick}
      >
        <PortfolioIcon type="courses" size={mq.XS ? 40 : 56} />
      </PortfolioSectionHeader>

      {Object.keys(collegeCourses).length === 0 ? (
        <div className={styles.addMoreCourses}>
          <h2>Don&#39;t be modest</h2>
          <p>Add some courses to start earning micro-scholarships!</p>
          <FormButton color="primary" onClick={handleAddClick}>
            Add a course
          </FormButton>
        </div>
      ) : (
        semesterGroups.map((semester) => {
          const [year, sem] = semester.split(' ');
          const courses = coursesForSemester[semester];
          const hasCoursesOverTenYears = currentYear - year > 10;

          return (
            <div key={semester}>
              {semester !== 'new' && semester !== undefined && (
                <SchoolYearHeader
                  schoolYear={`${year} | ${sem}`}
                  borderTop={false}
                  hasCourseYearWarning={hasCoursesOverTenYears}
                />
              )}
              <TwoColumnMasonry>
                {courses.map((course) => {
                  if (course.editing) {
                    return (
                      <div
                        key={course._id}
                        className={classNames('portfolio-sections column', {
                          editing: course.editing,
                        })}
                      >
                        <CollegeCourseFormCard course={course} />
                      </div>
                    );
                  } else {
                    return (
                      <div key={course._id} className="portfolio-sections column">
                        <Card id={course._id} active={!!course.scrollTo}>
                          <CollegeCourse course={course} />
                        </Card>
                      </div>
                    );
                  }
                })}
              </TwoColumnMasonry>
            </div>
          );
        })
      )}
    </Section>
  );
};

export default CollegeCourses;
